import Divider from "@components/common/Divider";
import React, { useEffect, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { useSelector } from "react-redux";
import { getResponsiblesState } from "@selectors/responsiblesSelectors";
import { getUserInfoState } from "@selectors/userInfoSelectors";
import responsibleHelper from "@utils/helpers/responsibleHelper";

import DashboardFilterButton from "../../DashboardFilterButton";
import {
  ActionsWrapper,
  CheckboxesWrapper,
  ErrorText,
  FilterOptionsWrapper,
  StyledActionButton,
  StyledDoneCheckbox,
  StyledText,
} from "../style";
import { LawsuitResponsible } from "@customTypes/user/responsible";
import { UserInfo } from "@customTypes/mainState";

type DashboardChartResponsibleFilterProps = {
  currentFilter: string[];
  setFilter: (selectedResponsibles: string[]) => void;
  minimumSelected?: number;
  maximumSelected?: number;
};

const DashboardChartResponsibleFilter: React.FC<DashboardChartResponsibleFilterProps> = ({
  currentFilter = [],
  setFilter,
  minimumSelected = 1,
  maximumSelected = 8,
}) => {
  const [feedback, setFeedback] = useState<{ message: string; isError: boolean } | undefined>();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedResponsible, setSelectedResponsible] = useState(
    currentFilter.slice(0, maximumSelected)
  );

  const activeResponsible = useSelector(getResponsiblesState);
  const userInfo = useSelector(getUserInfoState);

  const activeResponsibleExceptLoggedUser = activeResponsible
    .filter((responsible: LawsuitResponsible) => responsible.id !== userInfo.id)
    .sort(responsibleHelper.sortResposibleArrayByNickname);

  const toggleFilterOptions = () => setIsFilterOpen(!isFilterOpen);

  const handleSubmit = () => {
    const responsibleSet = new Set(selectedResponsible);

    if (responsibleSet.size < minimumSelected)
      return setFeedback({
        message: `Selecione pelo menos ${minimumSelected} acima`,
        isError: true,
      });

    setFeedback(undefined);
    setFilter(Array.from(responsibleSet));
    return toggleFilterOptions();
  };

  const filterLabel = () => {
    if (currentFilter.length === activeResponsible.length) return "Todos os responsáveis";
    return `${currentFilter.length} ${currentFilter.length > 1 ? "responsáveis" : "responsável"}`;
  };

  const disableCheckbox = (responsible: UserInfo | LawsuitResponsible) => {
    const found = selectedResponsible.find((selected) => {
      return selected === responsible.id;
    });

    return selectedResponsible.length >= maximumSelected && !found;
  };

  const handleCheckboxClick = (responsibleId: string) => {
    setSelectedResponsible((prevValues) =>
      prevValues.includes(responsibleId)
        ? prevValues.filter((v) => v !== responsibleId)
        : [...prevValues, responsibleId]
    );
  };

  useEffect(() => {
    if (
      selectedResponsible.length >= maximumSelected ||
      selectedResponsible.length === activeResponsible.length
    ) {
      setFeedback({
        message: "Você selecionou o máximo de pessoas",
        isError: false,
      });
    } else {
      setFeedback(undefined);
    }
  }, [selectedResponsible.length, activeResponsible.length, maximumSelected]);

  return (
    <OutsideClickHandler onOutsideClick={() => isFilterOpen && toggleFilterOptions()}>
      <div style={{ position: "relative" }}>
        <DashboardFilterButton onClick={toggleFilterOptions}>{filterLabel()}</DashboardFilterButton>
        <FilterOptionsWrapper isFilterOpen={isFilterOpen} align="left">
          <StyledText size="sm" bold uppercase>
            RESPONSÁVEIS
          </StyledText>
          <CheckboxesWrapper hasTextSection={!!feedback}>
            <StyledDoneCheckbox
              block
              onClick={() => handleCheckboxClick(userInfo.id)}
              disabled={disableCheckbox(userInfo)}
              validate={feedback?.isError}
              checked={!!selectedResponsible.find((selected) => selected === userInfo.id)}
            >
              {userInfo.name}
            </StyledDoneCheckbox>
            <Divider noMarginBottom noMarginTop style={{ marginBottom: "8px" }} />
            {activeResponsibleExceptLoggedUser.map((responsible: LawsuitResponsible) => (
              <StyledDoneCheckbox
                key={responsible.id}
                block
                onClick={() => handleCheckboxClick(responsible.id)}
                disabled={disableCheckbox(responsible)}
                validate={feedback?.isError}
                checked={!!selectedResponsible.find((selected) => selected === responsible.id)}
              >
                {responsible.name}
              </StyledDoneCheckbox>
            ))}
          </CheckboxesWrapper>
          {feedback && (
            <ErrorText color={feedback.isError ? "danger" : "gray"} size="sm">
              {feedback.message}
            </ErrorText>
          )}
          <Divider noMarginTop noMarginBottom />
          <ActionsWrapper>
            <StyledActionButton plain color="gray" onClick={() => toggleFilterOptions()}>
              Cancelar
            </StyledActionButton>
            <StyledActionButton plain type="submit" color="primary" onClick={handleSubmit}>
              Aplicar
            </StyledActionButton>
          </ActionsWrapper>
        </FilterOptionsWrapper>
      </div>
    </OutsideClickHandler>
  );
};

export default DashboardChartResponsibleFilter;
